import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  OrganizationCategory,
  ORGANIZATION_CATEGORY_LABELS,
  getActorDisplayName,
  withDirectionParticle,
} from '@safetyhub/shared';
import { Button, Card, Modal, SUB_CONFIRM_LAYER, TextButton, useConfirmDialog } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../lib/api';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { DEFAULT_MEMBER_ROLE_KEY, getMemberKindLabel, legacyRoleLabel, useSystemRoleOptions } from '../lib/roles';

const CATEGORY_LABELS = ORGANIZATION_CATEGORY_LABELS;

/** 멤버 추가 후보 검색 상한 — 화면은 그중 10명을 보이고, 이미 멤버인 사람을 빼고도 남게 넉넉히 받는다 */
const MEMBER_CANDIDATE_LIMIT = 30;

interface Organization {
  id: string; // 구독 id — 이 화면의 멤버십 endpoint(`/admin/subscriptions/:subscriptionId/*`)용
  companyId: string; // 회사 id — 카테고리 변경(`/admin/companies/:companyId/category`)용
  name: string;
  category: OrganizationCategory;
  createdAt: string;
  _count: { members: number; sites: number };
}

interface Member {
  memberId: string;
  /** ⚠️ 계정 없는 외부 연락처는 비어 있다 — 서버 `Member.userId`가 nullable이다 */
  userId: string;
  name: string;
  /** 소유자 성질(배정 축, P7-b-2 — 옛 `role`) */
  isOwner: boolean;
  /** ⚠️ false = 외부 연락처(조직 권한 없음, 옛 `role = null`) — `lib/memberships.ts` 주석 참조 */
  countsAsMember: boolean;
  createdAt: string;
  isDeactivated: boolean;
  auths: Array<{
    provider: string;
    providerId: string;
  }>;
}

interface MembershipHistoryItem {
  id: string;
  action: string;
  memberId: string | null;
  userName: string | null;
  userId: string | null;
  role: string | null;
  performedBy: string;
  performedAt: string;
  details: {
    oldValue: Record<string, unknown> | null;
    newValue: Record<string, unknown> | null;
  };
}

// 액션 유형 한글 라벨
const ACTION_LABELS: Record<string, string> = {
  ADMIN_REMOVE_ORGANIZATION_MEMBER: '관리자 제거',
  ADMIN_ADD_ORGANIZATION_MEMBER: '관리자 추가',
  ADMIN_UPDATE_ORGANIZATION_MEMBER_ROLE: '관리자 역할 변경',
  REMOVE_MEMBER: 'Owner 제거',
  LEAVE_ORGANIZATION: '본인 탈퇴',
  TRANSFER_OWNERSHIP: '소유권 이전',
  ORG_DEACTIVATION_MEMBER_REMOVED: '조직 비활성화',
};

interface OrganizationDetailModalProps {
  organization: Organization | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function OrganizationDetailModal({
  organization,
  isOpen,
  onClose,
}: OrganizationDetailModalProps) {
  const queryClient = useQueryClient();
  // 멤버 제거 확인 — 이 상세(z-[70]) 위에 뜨므로 한 층 위(`SUB_CONFIRM_LAYER`).
  const [confirm, confirmDialog] = useConfirmDialog();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  // 고른 순간의 표시 이름 — 고르면 검색어가 그 이름으로 바뀌어 후보 목록이 다시 조회되므로,
  // 후보 목록에서 다시 찾지 않고 여기 붙들어 둔다.
  const [selectedUserLabel, setSelectedUserLabel] = useState('');
  const [selectedRoleKey, setSelectedRoleKey] = useState<string>(DEFAULT_MEMBER_ROLE_KEY);
  const [editingRoleMemberId, setEditingRoleMemberId] = useState<string | null>(null);
  // 역할 select 후보 — 활성 시스템 역할(`/admin/rbac/roles`, P7-b-2: 옛 OWNER/SAFETY_MANAGER 2종 대체)
  const { options: roleOptions } = useSystemRoleOptions(isOpen);

  // 카테고리 변경 상태
  const [pendingCategory, setPendingCategory] = useState<OrganizationCategory | null>(null);
  const [isCategoryWarningOpen, setIsCategoryWarningOpen] = useState(false);
  const [localCategory, setLocalCategory] = useState<OrganizationCategory>(
    organization?.category ?? 'WORKPLACE',
  );

  // organization prop 변경 시 localCategory 동기화
  useEffect(() => {
    setLocalCategory(organization?.category ?? 'WORKPLACE');
  }, [organization?.category]);

  // 멤버 목록 조회
  const { data: membersData, isLoading: membersLoading, refetch: refetchMembers } = useQuery({
    queryKey: ['admin', 'organizations', organization?.id, 'members'],
    queryFn: () => adminApi.getSubscriptionMembers(organization!.id),
    enabled: !!organization?.id && isOpen,
  });

  // 멤버십 히스토리 조회
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['admin', 'organizations', organization?.id, 'membership-history'],
    queryFn: () => adminApi.getMembershipHistory(organization!.id),
    enabled: !!organization?.id && isOpen,
  });

  // 사용자 검색 (멤버 추가용) — 서버 `search`로 찾는다. 종전엔 첫 100명을 받아 그 안에서만
  // 걸러 그 밖의 사용자를 추가할 수 없었고, 사용자 화면과 같은 키(['admin','users'])라 캐시가
  // 서로 덮였다(2026-10-30). 검색어가 있을 때만 묻는다(목록은 검색어가 있어야 뜬다).
  // 탈퇴 대기 사용자는 후보에서 뺀다(`status: 'active'`) — 탈퇴 중인 사람을 조직에 새로 넣을 일이
  // 없다(사용자 결정 2026-10-30). 메시지 발송 대상은 안내를 보낼 수 있어야 해서 포함한 채 둔다.
  const debouncedUserSearch = useDebouncedValue(userSearch.trim());
  const { data: usersData } = useQuery({
    queryKey: ['admin', 'users', 'member-candidates', debouncedUserSearch],
    queryFn: () =>
      adminApi.getUsers({ limit: MEMBER_CANDIDATE_LIMIT, search: debouncedUserSearch, status: 'active' }),
    enabled: isAddingMember && !!debouncedUserSearch,
  });

  const members = membersData?.data || [];
  const membershipHistory: MembershipHistoryItem[] = historyData?.data || [];
  const candidateUsers = usersData?.data || [];

  // 이미 멤버인 사용자 ID 목록
  const memberUserIds = new Set(members.map((m) => m.userId));

  // 이미 멤버인 사용자 제외 (검색 축은 서버가 소유한다)
  const filteredUsers = candidateUsers.filter((user) => !memberUserIds.has(user.id));

  // 멤버 추가 mutation
  const addMemberMutation = useMutation({
    mutationFn: ({ userId, roleKey }: { userId: string; roleKey: string }) =>
      adminApi.addSubscriptionMember(organization!.id, userId, roleKey),
    onSuccess: (data) => {
      if (data.success) {
        setSuccess(`${data.data?.name}님이 조직에 추가되었습니다.`);
        setIsAddingMember(false);
        setSelectedUserId(null);
        setUserSearch('');
        setSelectedRoleKey(DEFAULT_MEMBER_ROLE_KEY);
        refetchMembers();
        queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
      } else {
        setError(data.error?.message || '멤버 추가에 실패했습니다.');
      }
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      setError(apiError.message);
    },
  });

  // 멤버 제거 mutation
  const removeMemberMutation = useMutation({
    mutationFn: (memberId: string) =>
      adminApi.removeSubscriptionMember(organization!.id, memberId),
    onSuccess: (data) => {
      if (data.success) {
        setSuccess('멤버가 조직에서 제거되었습니다.');
        refetchMembers();
        queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
      } else {
        setError(data.error?.message || '멤버 제거에 실패했습니다.');
      }
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      setError(apiError.message);
    },
  });

  // 카테고리 변경 mutation
  const updateCategoryMutation = useMutation({
    mutationFn: (category: OrganizationCategory) =>
      // 회사 카테고리 endpoint는 companyId를 받는다 — 구독 id를 넘기면 404가 난다.
      adminApi.updateCompanyCategory(organization!.companyId, category),
    onSuccess: (data) => {
      if (data.success) {
        const label = CATEGORY_LABELS[data.data?.category as OrganizationCategory] ?? data.data?.category;
        setSuccess(`조직 타입이 ${withDirectionParticle(label)} 변경되었습니다.`);
        setIsCategoryWarningOpen(false);
        setLocalCategory(data.data!.category as OrganizationCategory);
        setPendingCategory(null);
        queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
      } else {
        setError(data.error?.message || '조직 타입 변경에 실패했습니다.');
        setIsCategoryWarningOpen(false);
      }
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      setError(apiError.message);
      setIsCategoryWarningOpen(false);
    },
  });

  // 역할 변경 mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ memberId, roleKey }: { memberId: string; roleKey: string }) =>
      adminApi.updateSubscriptionMemberRole(organization!.id, memberId, roleKey),
    onSuccess: (data) => {
      if (data.success) {
        setSuccess('역할이 변경되었습니다.');
        setEditingRoleMemberId(null);
        refetchMembers();
      } else {
        setError(data.error?.message || '역할 변경에 실패했습니다.');
      }
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      setError(apiError.message);
    },
  });

  // 모달이 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSuccess(null);
      setIsAddingMember(false);
      setUserSearch('');
      setSelectedUserId(null);
      setSelectedRoleKey(DEFAULT_MEMBER_ROLE_KEY);
      setEditingRoleMemberId(null);
      setPendingCategory(null);
      setIsCategoryWarningOpen(false);
    }
  }, [isOpen]);

  // 메시지 자동 숨김
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  const handleAddMember = () => {
    if (!selectedUserId) {
      setError('사용자를 선택해주세요.');
      return;
    }
    setError(null);
    addMemberMutation.mutate({ userId: selectedUserId, roleKey: selectedRoleKey });
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (
      !(await confirm({
        title: '멤버 제거',
        body: `${memberName}님을 조직에서 제거하시겠습니까?`,
        confirmLabel: '제거',
        danger: true,
        ...SUB_CONFIRM_LAYER,
      }))
    )
      return;
    setError(null);
    removeMemberMutation.mutate(memberId);
  };

  const handleRoleChange = (memberId: string, roleKey: string) => {
    setError(null);
    updateRoleMutation.mutate({ memberId, roleKey });
  };

  const handleCategorySelectChange = (newCategory: OrganizationCategory) => {
    if (newCategory === localCategory) return;
    setPendingCategory(newCategory);
    setIsCategoryWarningOpen(true);
  };

  const handleCategoryConfirm = () => {
    if (!pendingCategory) return;
    setError(null);
    updateCategoryMutation.mutate(pendingCategory);
  };

  const handleCategoryCancel = () => {
    setIsCategoryWarningOpen(false);
    setPendingCategory(null);
  };

  const getLoginIdentifier = (member: Member) => {
    const idAuth = member.auths.find((a) => a.provider === 'ID');
    const emailAuth = member.auths.find((a) => a.provider === 'EMAIL');
    return idAuth?.providerId || emailAuth?.providerId || '-';
  };

  if (!organization) return null;

  const isPersonalOrg = organization.category === 'WORKSPACE';

  return (
    <Modal open={isOpen} onClose={onClose} title="조직 상세 정보" size="xl">
      <div className="space-y-6">
        {/* 기본 정보 */}
        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-3">기본 정보</h4>
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-xs text-gray-500">조직명</dt>
              <dd className="mt-1 text-sm text-gray-900 font-medium">{organization.name}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">유형</dt>
              <dd className="mt-1">
                {isPersonalOrg ? (
                  <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                    개인
                  </span>
                ) : (
                  <select
                    value={localCategory}
                    onChange={(e) => handleCategorySelectChange(e.target.value as OrganizationCategory)}
                    disabled={updateCategoryMutation.isPending}
                    className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50"
                  >
                    {(Object.keys(CATEGORY_LABELS) as OrganizationCategory[]).map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_LABELS[cat]}
                      </option>
                    ))}
                  </select>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">ID</dt>
              <dd className="mt-1 text-sm text-gray-500 font-mono text-xs">{organization.id}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">생성일</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(organization.createdAt).toLocaleDateString('ko-KR')}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">현장 수</dt>
              <dd className="mt-1 text-sm text-gray-900">{organization._count.sites}개</dd>
            </div>
          </dl>
        </div>

        {/* 메시지 */}
        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-card">{error}</div>
        )}
        {success && (
          <div className="p-3 bg-green-50 text-green-600 text-sm rounded-card">{success}</div>
        )}

        {/* 멤버 목록 */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-500">
              멤버 ({members.length}명)
            </h4>
            {!isPersonalOrg && !isAddingMember && (
              <Button variant="primary" size="xs" onClick={() => setIsAddingMember(true)}>
                멤버 추가
              </Button>
            )}
          </div>

          {/* 멤버 추가 폼 */}
          {isAddingMember && (
            <Card surface="soft" elevation="none" className="mb-4">
              <h5 className="text-sm font-medium text-gray-700 mb-3">새 멤버 추가</h5>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">사용자 검색</label>
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="이름 또는 아이디로 검색..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
                {userSearch && filteredUsers.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-card">
                    {filteredUsers.slice(0, 10).map((user) => (
                      <button
                        key={user.id}
                        onClick={() => {
                          setSelectedUserId(user.id);
                          setSelectedUserLabel(
                            getActorDisplayName({ name: user.member?.name, deletedAt: user.deletedAt }, '-'),
                          );
                          setUserSearch(user.member?.name ?? '');
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex justify-between items-center ${
                          selectedUserId === user.id ? 'bg-brand-50' : ''
                        }`}
                      >
                        <span>{getActorDisplayName({ name: user.member?.name, deletedAt: user.deletedAt }, '-')}</span>
                        <span className="text-xs text-gray-400">
                          {user.auths[0]?.providerId || ''}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {selectedUserId && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">
                      선택됨: {selectedUserLabel}
                    </span>
                    <TextButton
                      tone="neutral"
                      onClick={() => {
                        setSelectedUserId(null);
                        setUserSearch('');
                      }}
                      className="text-xs"
                    >
                      취소
                    </TextButton>
                  </div>
                )}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">역할</label>
                  <select
                    value={selectedRoleKey}
                    onChange={(e) => setSelectedRoleKey(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    {roleOptions.map((role) => (
                      <option key={role.key} value={role.key}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleAddMember}
                    disabled={!selectedUserId || addMemberMutation.isPending}
                  >
                    {addMemberMutation.isPending ? '추가 중...' : '추가'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setIsAddingMember(false);
                      setSelectedUserId(null);
                      setUserSearch('');
                      setError(null);
                    }}
                  >
                    취소
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* 멤버 테이블 */}
          {membersLoading ? (
            <div className="text-center py-8 text-gray-500">로딩 중...</div>
          ) : members.length === 0 ? (
            <div className="text-center py-8 text-gray-500">멤버가 없습니다.</div>
          ) : (
            <div className="border border-gray-200 rounded-card overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      이름
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      로그인 ID
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      역할
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      가입일
                    </th>
                    {!isPersonalOrg && (
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        액션
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {members.map((member) => (
                    <tr key={member.memberId} className={member.isDeactivated ? 'bg-gray-50' : ''}>
                      <td className="px-4 py-3 text-sm">
                        <span className={member.isDeactivated ? 'text-gray-400' : 'text-gray-900'}>
                          {getActorDisplayName({ name: member.name }, '-')}
                        </span>
                        {member.isDeactivated && (
                          <span className="ml-2 text-xs text-red-500">(탈퇴)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {getLoginIdentifier(member)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {/* ⚠️ countsAsMember=false = 외부 연락처(현장 담당자·외부 결재자, 옛 role null). 조직 권한이 없고,
                            역할 부여는 **초대(수락=동의)로만** 가능하다. 예전엔 이 배지가 클릭
                            가능했고 select의 `defaultValue={null}`이 OWNER를 보여줘, 한 번
                            고르면 계정도 없는 사람에게 OWNER가 부여됐다(서버 가드도 없었다). */}
                        {!member.countsAsMember ? (
                          <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-700">
                            외부 연락처
                          </span>
                        ) : editingRoleMemberId === member.memberId && !isPersonalOrg ? (
                          <select
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) handleRoleChange(member.memberId, e.target.value);
                            }}
                            disabled={updateRoleMutation.isPending}
                            className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-brand-500"
                            autoFocus
                            onBlur={() => setEditingRoleMemberId(null)}
                          >
                            <option value="" disabled>
                              역할 선택...
                            </option>
                            {roleOptions.map((role) => (
                              <option key={role.key} value={role.key}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                              member.isOwner
                                ? 'bg-brand-100 text-brand-700'
                                : 'bg-gray-100 text-gray-600'
                            } ${!isPersonalOrg ? 'cursor-pointer hover:opacity-80' : ''}`}
                            onClick={() => {
                              if (!isPersonalOrg) {
                                setEditingRoleMemberId(member.memberId);
                              }
                            }}
                          >
                            {getMemberKindLabel(member)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(member.createdAt).toLocaleDateString('ko-KR')}
                      </td>
                      {!isPersonalOrg && (
                        <td className="px-4 py-3 text-sm text-right">
                          <TextButton
                            tone="danger"
                            onClick={() => handleRemoveMember(member.memberId, member.name)}
                            disabled={removeMemberMutation.isPending}
                            className="text-xs"
                          >
                            제거
                          </TextButton>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 멤버십 히스토리 */}
        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-3">
            멤버십 변경 히스토리 ({membershipHistory.length}건)
          </h4>

          {historyLoading ? (
            <div className="text-center py-4 text-gray-500 text-sm">로딩 중...</div>
          ) : membershipHistory.length === 0 ? (
            <div className="text-center py-4 text-gray-400 text-sm">
              멤버십 변경 기록이 없습니다.
            </div>
          ) : (
            <div className="border border-gray-200 rounded-card overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      일시
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      유형
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      대상
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      역할
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      실행자
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-gray-50">
                  {membershipHistory.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(item.performedAt).toLocaleString('ko-KR', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                            item.action.includes('REMOVE') || item.action.includes('LEFT') || item.action.includes('DEACTIVATION')
                              ? 'bg-red-100 text-red-700'
                              : item.action.includes('ADD')
                              ? 'bg-green-100 text-green-700'
                              : 'bg-brand-100 text-brand-700'
                          }`}
                        >
                          {ACTION_LABELS[item.action] || item.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.userName || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {item.role && (
                          <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">
                            {legacyRoleLabel(item.role)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {item.performedBy}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 개인 조직 안내 */}
        {isPersonalOrg && (
          <div className="p-3 bg-yellow-50 text-yellow-700 text-sm rounded-card">
            개인 조직은 멤버를 추가하거나 제거할 수 없습니다.
          </div>
        )}
      </div>

      {/* 카테고리 변경 경고 모달 */}
      {/* 조직 상세(z-[70]) 위에 겹쳐 뜨는 sub 모달이라 한 층 위(z-[80]) + 낮춘 배경(겹침 알파 보정).
          변경 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다. */}
      {isCategoryWarningOpen && pendingCategory && (
        <Modal
          open
          onClose={handleCategoryCancel}
          title="조직 타입을 변경하시겠습니까?"
          size="md"
          overlayZClassName="z-[80]"
          overlayBgClassName="bg-black/25"
          closeOnOverlay={!updateCategoryMutation.isPending}
          closeOnEsc={!updateCategoryMutation.isPending}
          showCloseButton={!updateCategoryMutation.isPending}
          closeLocked={updateCategoryMutation.isPending}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={handleCategoryCancel}
                disabled={updateCategoryMutation.isPending}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={handleCategoryConfirm}
                disabled={updateCategoryMutation.isPending}
              >
                {updateCategoryMutation.isPending ? '변경 중...' : '확인'}
              </Button>
            </>
          }
        >
          <div className="space-y-2 text-sm text-gray-700 mb-4">
            <div className="flex gap-2">
              <span className="text-gray-500 w-16 flex-shrink-0">변경 전</span>
              <span className="font-medium">{CATEGORY_LABELS[localCategory]}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-gray-500 w-16 flex-shrink-0">변경 후</span>
              <span className="font-medium text-brand-700">{CATEGORY_LABELS[pendingCategory]}</span>
            </div>
          </div>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-card text-sm text-amber-800">
            <p className="font-medium mb-1">⚠️ 주의</p>
            <p>
              이 조직의 기존 TBM, 기술지도(TG), 안전관리상태보고서 등의 데이터는 그대로 유지됩니다.
              단, 조직 타입에 따라 UI에 노출되는 메뉴/필드가 달라질 수 있습니다.
            </p>
            {pendingCategory === 'SAFETY_MANAGEMENT_AGENCY' && (
              <p className="mt-1">안전관리전문기관으로 변경 시 현장 담당자 서명 UI가 활성화됩니다.</p>
            )}
            <p className="mt-1">변경 이력은 감사 로그에 기록됩니다.</p>
          </div>
        </Modal>
      )}
      {confirmDialog}
    </Modal>
  );
}
